import {produce} from 'immer'
import {AxiosResponse} from 'axios'

import {
  GetPoliciesParams,
  GetPoliciesResponseBody,
  PolicyContentExceptionsRequest,
  PolicyContentResponse,
  PolicyContentRestrictionsRequest,
  AssignedPolicyTypes,
  ExceptionType,
  CloudAppListTypes,
  CloudAppResponseType,
  CloudAppSaveList,
  PolicyNamePayloadType,
  PoliciesClashesPayloadInterface,
  PoliciesClashesInterface,
  UpdatePolicyContentExceptionsPayload,
  GenericMessageResponse,
  UpdatePolicyContentRestrictionsPayload,
  UpdateUrlFiltersPayload,
  DeleteCategoriesPayload,
  DeleteApplicationBypassPayload,
  CreateCustomCategoriesPayload,
  CreateCustomCategoryResult,
  DeleteCustomCategoryResult,
  CustomCategoryAction,
  CustomCategoryError,
  PolicyAssignmentResponse,
  DeleteUrlBypassPayload,
  GetApplicationBypassResponse,
  GetDLPConfigResponse,
  UpdateDLPRestrictionsPayload,
  UpdateDLPExceptionsPayload,
  GetPolicyTesterAPIParams,
  PolicyTesterResponse,
  GetDLPBypassResponse,
  UpdateDLPBypassPayload,
  UpdateDLPNotificationsPayload,
  UpdateSSLInspectionSchemaPayload,
  GetCustomCategoryUrlsResponse,
  GetUrlBypassResponse,
  AddCustomBypassToAllPoliciesPayload,
  UpdateApplicationBypassPayload,
  UpdateUrlBypassPayload,
  UpdatePolicyAssignmentPayload,
} from 'interfaces'
import {unwrapAPIError} from 'utils'
import {getFlightdeckBaseUrl, requestHandler} from '../HTTP'
import {EndPoints, HttpMethods} from '../HTTP/HTTP.types'

const flightDeckBaseUrl = getFlightdeckBaseUrl()

const getPolicyRoute = (endpoint: EndPoints, policyName: string): string =>
  endpoint.replace('{policyName}', encodeURIComponent(policyName))

const mapPolicyAssignmentResponse = (
  response: PolicyAssignmentResponse,
): AssignedPolicyTypes[] => [
  ...response.data.groups.map(group => ({
    emailId: group.email,
    name: group.name,
    membersCount: String(group.membersCount),
    type: ExceptionType.Group,
  })),
  ...response.data.users.map(user => ({
    emailId: user.email,
    name: user.name,
    type: ExceptionType.User,
  })),
]

const mapPolicyAssignmentRequest = ({
  usersGroups,
}: UpdatePolicyAssignmentPayload): {users: string[]; groups: string[]} => ({
  users: usersGroups
    .filter(usersGroup => usersGroup.type === ExceptionType.User)
    .map(user => user.emailId),
  groups: usersGroups
    .filter(usersGroup => usersGroup.type === ExceptionType.Group)
    .map(group => group.emailId),
})

const getPolicies = async (
  params: GetPoliciesParams,
): Promise<AxiosResponse<GetPoliciesResponseBody>> => {
  try {
    return await requestHandler<GetPoliciesResponseBody>({
      method: HttpMethods.GET,
      url: `${flightDeckBaseUrl}${EndPoints.Policies}`,
      options: {params},
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    throw new Error(String(errorValue))
  }
}

const getPolicyContent = async (
  policyName: string,
): Promise<PolicyContentResponse> => {
  try {
    const res = await requestHandler<PolicyContentResponse>({
      method: HttpMethods.GET,
      url: `${flightDeckBaseUrl}/policies/${encodeURIComponent(
        policyName,
      )}/content`,
      options: {
        params: {
          includeParent: true,
        },
      },
    })
    return res.data
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    throw new Error(String(errorValue))
  }
}

const getCustomCategoryUrls = async (
  categoryName: string,
): Promise<GetCustomCategoryUrlsResponse> => {
  try {
    const {data} = await requestHandler<GetCustomCategoryUrlsResponse>({
      method: HttpMethods.GET,
      url: `${flightDeckBaseUrl}${
        EndPoints.CustomCategories
      }/${encodeURIComponent(categoryName)}/urls`,
    })

    return data
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    throw new Error(String(errorValue))
  }
}

const createPolicy = async (
  policyName: string,
): Promise<GenericMessageResponse> => {
  try {
    const res = await requestHandler<GenericMessageResponse>({
      method: HttpMethods.POST,
      url: `${flightDeckBaseUrl}${EndPoints.Policies}/${encodeURIComponent(
        policyName,
      )}`,
    })
    return res.data
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const updatePolicyContentExceptions = async ({
  policyName,
  data,
}: UpdatePolicyContentExceptionsPayload): Promise<
  AxiosResponse<GenericMessageResponse>
> => {
  try {
    return await requestHandler<
      GenericMessageResponse,
      PolicyContentExceptionsRequest
    >({
      method: HttpMethods.PUT,
      url: `${flightDeckBaseUrl}/policies/${encodeURIComponent(
        policyName,
      )}/content/exceptions`,
      data,
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    throw new Error(String(errorValue))
  }
}

const updatePolicyContentRestrictions = async ({
  policyName,
  data,
}: UpdatePolicyContentRestrictionsPayload): Promise<
  AxiosResponse<GenericMessageResponse>
> => {
  try {
    return await requestHandler<
      GenericMessageResponse,
      PolicyContentRestrictionsRequest
    >({
      method: HttpMethods.PUT,
      url: `${flightDeckBaseUrl}/policies/${encodeURIComponent(
        policyName,
      )}/content/restrictions`,
      data,
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const updatePolicyURLFilters = async ({
  data,
}: UpdateUrlFiltersPayload): Promise<AxiosResponse<{message: string}>> => {
  try {
    return await requestHandler({
      method: HttpMethods.PUT,
      url: `${flightDeckBaseUrl}/custom_categories/urls`,
      data: {data},
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const customCategoriesRequest = async (
  method: HttpMethods.POST | HttpMethods.DELETE,
  categoryNames: string[],
  action: CustomCategoryAction,
): Promise<CreateCustomCategoryResult> => {
  const results = await Promise.allSettled(
    categoryNames.map(name =>
      requestHandler<GenericMessageResponse>({
        method,
        url: `${flightDeckBaseUrl}${
          EndPoints.CustomCategories
        }/${encodeURIComponent(name)}`,
      }),
    ),
  )

  const succeeded: string[] = []
  const failed: {name: string; error: string}[] = []

  results.forEach((result, index) => {
    const name = categoryNames[index]
    if (result.status === 'fulfilled') {
      succeeded.push(name)
    } else {
      const errorValue = unwrapAPIError(result.reason)
      failed.push({name, error: String(errorValue)})
    }
  })

  if (failed.length > 0) {
    throw new CustomCategoryError(
      `Failed to ${action} categories: ${failed.map(f => f.name).join(', ')}`,
      {succeeded, failed},
    )
  }

  return {succeeded, failed}
}

const deleteCustomCategories = ({
  data,
}: DeleteCategoriesPayload): Promise<DeleteCustomCategoryResult> =>
  customCategoriesRequest(
    HttpMethods.DELETE,
    data.categoryNames,
    CustomCategoryAction.Delete,
  )

const createCustomCategories = ({
  data,
}: CreateCustomCategoriesPayload): Promise<CreateCustomCategoryResult> =>
  customCategoriesRequest(
    HttpMethods.POST,
    data.categoryNames,
    CustomCategoryAction.Create,
  )

const getUrlBypass = async (
  policyName: string,
): Promise<GetUrlBypassResponse> => {
  try {
    const {data} = await requestHandler<GetUrlBypassResponse>({
      method: HttpMethods.GET,
      url: `${flightDeckBaseUrl}${getPolicyRoute(
        EndPoints.UrlBypass,
        policyName,
      )}`,
    })
    return data
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    throw new Error(String(errorValue))
  }
}

const getApplicationBypass = async (
  policyName: string,
): Promise<GetApplicationBypassResponse> => {
  try {
    const {data} = await requestHandler<GetApplicationBypassResponse>({
      method: HttpMethods.GET,
      url: `${flightDeckBaseUrl}${getPolicyRoute(
        EndPoints.ApplicationBypass,
        policyName,
      )}`,
    })
    return data
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    throw new Error(String(errorValue))
  }
}

const updateUrlBypass = async (
  policyName: string,
  payload: UpdateUrlBypassPayload,
): Promise<AxiosResponse<GenericMessageResponse>> => {
  try {
    return await requestHandler<GenericMessageResponse, UpdateUrlBypassPayload>(
      {
        method: HttpMethods.PUT,
        url: `${flightDeckBaseUrl}${getPolicyRoute(
          EndPoints.UrlBypass,
          policyName,
        )}`,
        data: payload,
      },
    )
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    throw new Error(String(errorValue))
  }
}

const deleteUrlBypass = async (
  policyName: string,
  payload: DeleteUrlBypassPayload,
): Promise<AxiosResponse<GenericMessageResponse>> => {
  try {
    return await requestHandler<GenericMessageResponse, DeleteUrlBypassPayload>(
      {
        method: HttpMethods.DELETE,
        url: `${flightDeckBaseUrl}${getPolicyRoute(
          EndPoints.UrlBypass,
          policyName,
        )}`,
        data: payload,
      },
    )
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    throw new Error(String(errorValue))
  }
}

const updateApplicationBypass = async (
  policyName: string,
  payload: UpdateApplicationBypassPayload,
): Promise<AxiosResponse<GenericMessageResponse>> => {
  try {
    return await requestHandler<
      GenericMessageResponse,
      UpdateApplicationBypassPayload
    >({
      method: HttpMethods.PUT,
      url: `${flightDeckBaseUrl}${getPolicyRoute(
        EndPoints.ApplicationBypass,
        policyName,
      )}`,
      data: payload,
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    throw new Error(String(errorValue))
  }
}

const deleteApplicationBypass = async (
  policyName: string,
  payload: DeleteApplicationBypassPayload,
): Promise<AxiosResponse<GenericMessageResponse>> => {
  try {
    return await requestHandler<
      GenericMessageResponse,
      DeleteApplicationBypassPayload
    >({
      method: HttpMethods.DELETE,
      url: `${flightDeckBaseUrl}${getPolicyRoute(
        EndPoints.ApplicationBypass,
        policyName,
      )}`,
      data: payload,
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    throw new Error(String(errorValue))
  }
}

const addCustomBypassToAllPolicies = async (
  payload: AddCustomBypassToAllPoliciesPayload,
): Promise<AxiosResponse<GenericMessageResponse>> => {
  try {
    return await requestHandler<
      GenericMessageResponse,
      AddCustomBypassToAllPoliciesPayload
    >({
      method: HttpMethods.POST,
      url: `${flightDeckBaseUrl}${EndPoints.BulkCustomBypass}`,
      data: payload,
      options: {timeout: 30000},
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    throw new Error(errorValue)
  }
}

const deletePolicy = async (
  policyName: string,
): Promise<GenericMessageResponse> => {
  try {
    const res = await requestHandler<GenericMessageResponse>({
      method: HttpMethods.DELETE,
      url: `${flightDeckBaseUrl}${EndPoints.Policies}/${encodeURIComponent(
        policyName,
      )}`,
    })
    return res.data
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getPolicyAssignment = async (
  policyName: string,
): Promise<
  AxiosResponse<{
    message: string
    Items: AssignedPolicyTypes[]
  }>
> => {
  try {
    const response = await requestHandler<PolicyAssignmentResponse>({
      method: HttpMethods.GET,
      url: `${flightDeckBaseUrl}${getPolicyRoute(
        EndPoints.PolicyAssignments,
        policyName,
      )}`,
    })

    return {
      ...response,
      data: {
        message: '',
        Items: mapPolicyAssignmentResponse(response.data),
      },
    }
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    throw new Error(String(errorValue))
  }
}

const updatePolicyAssignment = async (
  payload: UpdatePolicyAssignmentPayload,
): Promise<AxiosResponse<GenericMessageResponse>> => {
  try {
    return await requestHandler<
      GenericMessageResponse,
      {users: string[]; groups: string[]}
    >({
      method: HttpMethods.PUT,
      url: `${flightDeckBaseUrl}${getPolicyRoute(
        EndPoints.PolicyAssignments,
        payload.policyName,
      )}`,
      data: mapPolicyAssignmentRequest(payload),
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    throw new Error(String(errorValue))
  }
}

const getFlightdeckPolicyTesterDetails = async (
  params: GetPolicyTesterAPIParams,
): Promise<PolicyTesterResponse> => {
  try {
    const {data} = await requestHandler<PolicyTesterResponse>({
      method: HttpMethods.GET,
      url: `${flightDeckBaseUrl}${EndPoints.PoliciesTester}`,
      options: {
        params,
      },
    })
    return data
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const updateCloudApp = async (payload: {
  policyName: string
  cac: CloudAppListTypes | CloudAppSaveList | string
}): Promise<AxiosResponse<{message: string}>> => {
  try {
    return await requestHandler<{message: string}>({
      method: HttpMethods.POST,
      url: EndPoints.UpdateCac,
      data: payload,
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getCloudApp = async (
  payload: PolicyNamePayloadType,
): Promise<AxiosResponse<CloudAppResponseType>> => {
  try {
    const response = await requestHandler<CloudAppResponseType>({
      method: HttpMethods.GET,
      url: `${EndPoints.GetCloudApp}${encodeURIComponent(payload.policyName)}`,
    })
    // TODO: Remove this once backend has removed consumer_info from the response
    const responseData = produce(response, draft => {
      delete draft.data.cac.google?.consumer_info
      delete draft.data.cac.o365?.consumer_info
    })
    return responseData
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getPoliciesClashes = async ({
  policyName,
  lastEvaluatedKey,
}: PoliciesClashesPayloadInterface): Promise<
  AxiosResponse<PoliciesClashesInterface>
> => {
  try {
    return await requestHandler<PoliciesClashesInterface>({
      method: HttpMethods.POST,
      url: `${EndPoints.GetPolicyClashGroups}?policyName=${encodeURIComponent(
        policyName,
      )}`,
      data: lastEvaluatedKey,
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getDLPConfig = async (
  policyName: string,
): Promise<GetDLPConfigResponse> => {
  try {
    const {data} = await requestHandler<GetDLPConfigResponse>({
      method: HttpMethods.GET,
      url: `${flightDeckBaseUrl}/policies/${encodeURIComponent(policyName)}${
        EndPoints.DLP
      }`,
    })
    return data
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getDLPBypass = async (
  policyName: string,
): Promise<GetDLPBypassResponse> => {
  try {
    const {data} = await requestHandler<GetDLPBypassResponse>({
      method: HttpMethods.GET,
      url: `${flightDeckBaseUrl}/policies/${encodeURIComponent(policyName)}${
        EndPoints.DLPBypass
      }`,
    })
    return data
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const updateDLPBypass = async (
  policyName: string,
  payload: UpdateDLPBypassPayload,
): Promise<AxiosResponse<GenericMessageResponse>> => {
  try {
    return await requestHandler<GenericMessageResponse>({
      method: HttpMethods.PUT,
      url: `${flightDeckBaseUrl}/policies/${encodeURIComponent(policyName)}${
        EndPoints.DLPBypass
      }`,
      data: {...payload},
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const updateDLPRestrictions = async (
  policyName: string,
  payload: UpdateDLPRestrictionsPayload,
): Promise<AxiosResponse<GenericMessageResponse>> => {
  try {
    return await requestHandler<GenericMessageResponse>({
      method: HttpMethods.PUT,
      url: `${flightDeckBaseUrl}/policies/${encodeURIComponent(policyName)}${
        EndPoints.DLPRestrictions
      }`,
      data: {data: payload},
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const updateDLPExceptions = async (
  policyName: string,
  payload: UpdateDLPExceptionsPayload,
): Promise<AxiosResponse<GenericMessageResponse>> => {
  try {
    return await requestHandler<GenericMessageResponse>({
      method: HttpMethods.PUT,
      url: `${flightDeckBaseUrl}/policies/${encodeURIComponent(policyName)}${
        EndPoints.DLPExceptions
      }`,
      data: {data: payload},
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const updateDLPNotifications = async (
  policyName: string,
  payload: UpdateDLPNotificationsPayload,
): Promise<AxiosResponse<GenericMessageResponse>> => {
  try {
    return await requestHandler<GenericMessageResponse>({
      method: HttpMethods.PUT,
      url: `${flightDeckBaseUrl}/policies/${encodeURIComponent(policyName)}${
        EndPoints.DLPNotifications
      }`,
      data: {...payload},
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const updateSSLInspection = async (
  policyName: string,
  payload: UpdateSSLInspectionSchemaPayload,
): Promise<AxiosResponse<GenericMessageResponse>> => {
  try {
    return await requestHandler<GenericMessageResponse>({
      method: HttpMethods.PUT,
      url: `${flightDeckBaseUrl}${EndPoints.SSLInspection.replace(
        '{policyName}',
        encodeURIComponent(policyName),
      )}`,
      data: {...payload},
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    throw errorValue
  }
}

export const PoliciesService = {
  getPolicies,
  getPolicyContent,
  getCustomCategoryUrls,
  createPolicy,
  updatePolicyContentExceptions,
  updatePolicyContentRestrictions,
  updatePolicyURLFilters,
  getUrlBypass,
  getApplicationBypass,
  updateUrlBypass,
  deleteUrlBypass,
  updateApplicationBypass,
  deleteApplicationBypass,
  addCustomBypassToAllPolicies,
  deletePolicy,
  getFlightdeckPolicyTesterDetails,
  getCloudApp,
  updateCloudApp,
  getPoliciesClashes,
  deleteCustomCategories,
  createCustomCategories,
  getPolicyAssignment,
  updatePolicyAssignment,
  getDLPBypass,
  getDLPConfig,
  updateDLPBypass,
  updateDLPExceptions,
  updateDLPRestrictions,
  updateDLPNotifications,
  updateSSLInspection,
}
